import { TimeUtil } from '../utils/time';
import { BacklinksOverviewData, ChartDataPoint, TldDistribution, Backlink, ReferringDomain, TopAnchor, TopCountry } from './types';
import { backlinksOverviewSections, overviewChartDataTooltips } from './settings';


export function getBacklinksOverviewForInterval(overview: BacklinksOverviewData, startDateStr: string, endDateStr: string) {
    const startDateMoment = TimeUtil.toDate(startDateStr);
    const endDateMoment = TimeUtil.toDate(endDateStr);

    const overviewSettingsKeys = Object.values(backlinksOverviewSections).map((overviewSection) => overviewSection.key);

    let overviewForInterval: BacklinksOverviewData = { total_backlinks: [], referring_domains: [], domain_rank: [], follow_links: [], nofollow_links: [] };

    overviewSettingsKeys.forEach((key) => {
        overview[key].forEach((overviewDataPoint) => {
            const overviewMoment = TimeUtil.toDate(overviewDataPoint.c_at);

            if (overviewMoment.isSameOrAfter(startDateMoment) && overviewMoment.isSameOrBefore(endDateMoment)) {
                overviewForInterval[key].push(overviewDataPoint);
            }
        });
    });

    return overviewForInterval;
}


export function getChartData(overview: BacklinksOverviewData, resourceType: keyof BacklinksOverviewData): ChartDataPoint[] {
    let data: ChartDataPoint[] = overview && overview[resourceType] ? overview[resourceType].map((item) => ({ ...item, type: overviewChartDataTooltips[resourceType] })) : [];
    if (!data || data.length === 0) return [];

    const result: ChartDataPoint[] = [];

    const lastDate = TimeUtil.toDate(data[data.length - 1].c_at)
    let targetDate = lastDate; // week endpoint reference

    // work backwards, we want up to 4 weekly points
    for (let i = 0; i < 4; i++) {
        // find the closest date ≤ targetDate
        let candidate: ChartDataPoint | undefined;
        for (let j = data.length - 1; j >= 0; j--) {
            const current = TimeUtil.toDate(data[j].c_at);
            if (current.isSame(targetDate, 'day') || current.isBefore(targetDate, 'day')) {
                candidate = data[j];
                break;
            }
        }

        if (!candidate) break; // no earlier data → stop

        result.unshift(candidate); // unshift to keep chronological order

        // move 1 week earlier for next target
        targetDate = targetDate.subtract(1, 'week');
    }

    return result;
}

export const getResourceToExportAsJson = (data: Backlink[] | ReferringDomain[] | TldDistribution[] | TopAnchor[] | TopCountry[]) => {
    if (typeof data !== 'undefined') {
        return JSON.stringify(data);
    }
};

export const getResourceToExportAsCsv = (data: Backlink[] | ReferringDomain[] | TldDistribution[] | TopAnchor[] | TopCountry[]) => {
    if (typeof data !== 'undefined') {
        let csv = '';
        try {
            let columns = Object.keys(data[0]);
            columns.forEach((column) => {
                csv = `${csv}${column};`;
            });
            csv = csv.replace(/.$/, '\n');

            data.forEach((element) => {
                columns.forEach((column) => {
                    let value = element[column as keyof typeof element] || '';
                    csv = `${csv}${value};`;
                });
                csv = csv.replace(/.$/, '\n');
            });
        } catch (e) { }
        return csv;
    }
};

export function getYAxisMax(chartData: ChartDataPoint[]) {
    const values = chartData.map((item) => item['value']);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min;

    // If all values are the same, add small padding
    if (range === 0) {
        return max === 0 ? 10 : max + Math.max(5, max * 0.1);
    }

    // For very small ranges, add proportional padding
    if (range <= 10) {
        return max + Math.max(range * 0.5, 2);
    }

    // Calculate dynamic padding based on the magnitude of the range
    const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
    const normalizedRange = range / magnitude;

    let paddingMultiplier;
    if (normalizedRange <= 1) {
        paddingMultiplier = 0.5; // 50% of range for very small ranges
    } else if (normalizedRange <= 5) {
        paddingMultiplier = 0.2; // 20% of range
    } else {
        paddingMultiplier = 0.1; // 10% of range for larger ranges
    }

    return max + (range * paddingMultiplier);
}

export function getYAxisMin(chartData: ChartDataPoint[]) {
    const values = chartData.map((item) => item['value']);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    // If all values are the same, subtract small padding but keep above 0
    if (range === 0) {
        return Math.max(0, min - Math.max(5, min * 0.1));
    }

    // For very small ranges, subtract proportional padding
    if (range <= 10) {
        const padding = Math.max(range * 0.5, 2);
        return Math.max(0, min - padding);
    }

    // Calculate dynamic padding based on the magnitude of the range
    const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
    const normalizedRange = range / magnitude;

    let paddingMultiplier;
    if (normalizedRange <= 1) {
        paddingMultiplier = 0.5; // 50% of range for very small ranges
    } else if (normalizedRange <= 5) {
        paddingMultiplier = 0.2; // 20% of range
    } else {
        paddingMultiplier = 0.1; // 10% of range for larger ranges
    }

    const padding = range * paddingMultiplier;
    return Math.max(0, min - padding);
}

export function tickFormatter(value: number) {
    let val = value;
    if (val >= 1000000000) {
        val = val / 1000000000;
        return val.toFixed(1) + ' B';
    }
    if (val >= 1000000) {
        val = val / 1000000;
        return val.toFixed(1) + ' M';
    }
    if (val >= 100000) {
        val = val / 1000000;
        return val.toFixed(1) + ' M';
    }
    if (val >= 1000) {
        val = val / 1000;

        if (val < 10) {
            return value.toLocaleString();
            //return val.toFixed(1) + ' K';            
        }

        return val.toFixed(0) + ' K';
    }

    return val;
}


/**

export function getYAxisMax(chartData: ChartDataPoint[]) {
    let values = chartData.map((item) => item['value']);

    let max = Math.max.apply(Math, values);
    let min = Math.min.apply(Math, values);
    let maxStr = max.toString();

    if (max > 100) {
        return max + 10 ** (maxStr.length - 2);
    }

    if (max - min === 1) {
        return max + 1;
    }

    return max + 5;
}

export function getYAxisMin(chartData: ChartDataPoint[]) {
    var values = chartData.map((item) => item['value']);

    var min = Math.min.apply(Math, values);
    var max = Math.max.apply(Math, values);
    var minStr = min.toString();

    if (min > 100) {
        return min - 10 ** (minStr.length - 2);
    }

    if (max - min === 1 && min - 1 > 0) {
        return min - 1;
    }

    if (min - 5 > 0) {
        return min - 5;
    }
    return 0;
}

export function tickFormatter(val: number) {
    if (val >= 1000000000) {
        val = val / 1000000000;
        return val.toFixed(1) + ' B';
    }
    if (val >= 1000000) {
        val = val / 1000000;
        return val.toFixed(1) + ' M';
    }
    if (val >= 100000) {
        val = val / 1000000;
        return val.toFixed(1) + ' M';
    }
    if (val >= 1000) {
        val = val / 1000;

        if (val < 10) {
            return val.toFixed(1) + ' K';
        }

        return val.toFixed(0) + ' K';
    }

    return val;
}

*/